/**
 * The words a slider position is worth.
 *
 * The scale is always -3..+3 and the scorer never reads any of this — but the
 * person answering does, and "Somewhat love" is not a sentence anybody says.
 * A ladder spells out all seven positions instead, so every stop reads as an
 * answer a human would give: "Rarely", "Undecided", "Nothing off-limits".
 *
 * Deliberately a closed set rather than seven free-text fields per question.
 * Writing seven phrases that get *monotonically* stronger is fiddly, and a
 * catalogue where each question invented its own is a catalogue where the same
 * position means something slightly different from row to row. Picking from ten
 * is a select in /admin and one word in the seed.
 *
 * Adding one is adding an entry here. The union type, the /admin select and
 * the seed's validation all derive from this object, so nothing else has to
 * hear about it.
 */

import { SCALE_MIN, type ScaleValue } from "./scale";

export interface Ladder {
  /** Names the axis in /admin, where the steps are shown alongside. */
  label: string;
  /** One phrase per position, -3 first, +3 last. Strictly increasing. */
  steps: readonly [string, string, string, string, string, string, string];
}

/**
 * Ten axes, which is enough for the whole catalogue.
 *
 * The middle of every one has to mean "this doesn't move me", because that's
 * what the scorer takes it for — a 0 carries no weight. A ladder whose centre
 * read as a real position ("Sometimes", say, if the question were about
 * frequency of something that matters) would quietly zero out an opinion.
 */
export const LADDERS = {
  taste: {
    label: "Hate → Love",
    steps: [
      "Hate it",
      "Dislike it",
      "Not for me",
      "Don't mind",
      "Like it",
      "Love it",
      "Obsessed",
    ],
  },

  agreement: {
    label: "No → Yes",
    steps: [
      "Absolutely not",
      "No",
      "Probably not",
      "Undecided",
      "Probably",
      "Yes",
      "Absolutely",
    ],
  },

  frequency: {
    label: "Never → Constantly",
    steps: [
      "Never",
      "Almost never",
      "Rarely",
      "Sometimes",
      "Often",
      "Very often",
      "Constantly",
    ],
  },

  comfort: {
    label: "Never → Enthusiastic",
    steps: [
      "Never",
      "Very uncomfortable",
      "Uncomfortable",
      "Curious",
      "Comfortable",
      "Keen",
      "Enthusiastic",
    ],
  },

  openness: {
    label: "Private → Open",
    steps: [
      "Never discussed",
      "Very private",
      "Private",
      "Depends",
      "Fairly open",
      "Open",
      "Nothing off-limits",
    ],
  },

  planning: {
    label: "Spontaneous → Planned",
    steps: [
      "Never plan",
      "Mostly spontaneous",
      "Loose plans",
      "Either way",
      "Some structure",
      "Planned",
      "Every detail planned",
    ],
  },

  closeness: {
    label: "Apart → Together",
    steps: [
      "Very separate",
      "Lots of space",
      "Some space",
      "Either way",
      "Close",
      "Very close",
      "Inseparable",
    ],
  },

  drive: {
    label: "Relaxed → Driven",
    steps: [
      "No ambition",
      "Very relaxed",
      "Relaxed",
      "Balanced",
      "Motivated",
      "Driven",
      "Relentless",
    ],
  },

  politics: {
    label: "Left → Right",
    steps: [
      "Far left",
      "Left",
      "Centre-left",
      "Centre",
      "Centre-right",
      "Right",
      "Far right",
    ],
  },

  faith: {
    label: "Secular → Devout",
    steps: [
      "Atheist",
      "Not religious",
      "Sceptical",
      "Unsure",
      "Somewhat religious",
      "Religious",
      "Devout",
    ],
  },
} as const satisfies Record<string, Ladder>;

export type LadderId = keyof typeof LADDERS;

/** For the /admin select and anything else that wants to walk the set. */
export const LADDER_IDS = Object.keys(LADDERS) as LadderId[];

/** What a question uses when it doesn't name one. */
export const DEFAULT_LADDER: LadderId = "taste";

export function isLadderId(v: unknown): v is LadderId {
  return typeof v === "string" && Object.hasOwn(LADDERS, v);
}

/**
 * Resolves an id to its ladder, falling back rather than throwing.
 *
 * A question stored with a ladder that has since been removed from the set
 * should render as something slightly wrong, not blank the row or take the
 * page down — its answers are still perfectly good numbers.
 */
export function ladderOf(id: string | null | undefined): Ladder {
  return isLadderId(id) ? LADDERS[id] : LADDERS[DEFAULT_LADDER];
}

/**
 * The phrase for one position on one question.
 *
 *   ladderLabel("taste", 3)      -> "Obsessed"
 *   ladderLabel("agreement", -1) -> "Probably not"
 *   ladderLabel("frequency", 0)  -> "Sometimes"
 */
export function ladderLabel(id: string | null | undefined, value: ScaleValue): string {
  return ladderOf(id).steps[value - SCALE_MIN]!;
}

/** The two ends, for the captions either side of the track. */
export function ladderPoles(id: string | null | undefined): [string, string] {
  const { steps } = ladderOf(id);
  return [steps[0], steps[6]];
}
